import React, { useEffect, useState } from 'react';
import {
  defaultThemeFamilyId,
  resolveThemeVariant,
  themeFamilyStorageKey,
  themeStorageKey,
  type ThemeFamilyId,
  type ThemeSelection,
} from '@/lib/theme/themeRegistry';
import { ThemeContext } from './themeContext';

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeSelection>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(themeStorageKey) as ThemeSelection | null;
      return stored || 'system';
    }
    return 'system';
  });
  const [themeFamilyId, setThemeFamilyState] = useState<ThemeFamilyId>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(themeFamilyStorageKey) as ThemeFamilyId | null;
      return stored || defaultThemeFamilyId;
    }
    return defaultThemeFamilyId;
  });

  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>('light');

  useEffect(() => {
    const root = window.document.documentElement;

    const updateTheme = () => {
      const systemMode: 'light' | 'dark' = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      const activeVariant = resolveThemeVariant(themeFamilyId, theme, systemMode);

      setResolvedTheme(activeVariant.mode);

      root.classList.remove('light', 'dark');
      root.classList.add(activeVariant.rootClass);
      root.dataset.themeFamily = activeVariant.familyId;
      root.dataset.themeMode = activeVariant.mode;
    };

    updateTheme();

    // Listen for system theme changes.
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (theme === 'system') {
        updateTheme();
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme, themeFamilyId]);

  const setTheme = (newTheme: ThemeSelection) => {
    setThemeState(newTheme);
    localStorage.setItem(themeStorageKey, newTheme);
  };

  const setThemeFamilyId = (newThemeFamilyId: ThemeFamilyId) => {
    setThemeFamilyState(newThemeFamilyId);
    localStorage.setItem(themeFamilyStorageKey, newThemeFamilyId);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, themeFamilyId, setThemeFamilyId, resolvedTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
